import { DrawerSnapPoints } from '@repo/ui-kit/components/modules/Drawer';
import { create } from 'zustand';

import { FooterMobilePanel, MobileDrawerStoreState } from './types';

type SnapPointsConfig = {
  initial: DrawerSnapPoints;
  available?: DrawerSnapPoints[];
};

const DEFAULT_SNAP_POINTS = [
  DrawerSnapPoints.HALF_OPENED,
  DrawerSnapPoints.FULL_OPENED,
];

const PANEL_CONFIGURATIONS: Record<string, SnapPointsConfig> = {
  [FooterMobilePanel.Add]: {
    initial: DrawerSnapPoints.FULL_OPENED,
    available: [DrawerSnapPoints.FULL_OPENED],
  },
  [FooterMobilePanel.Template]: {
    initial: DrawerSnapPoints.FULL_OPENED,
  },
  [FooterMobilePanel.KittlAi]: {
    initial: DrawerSnapPoints.FULL_OPENED,
  },
  default: {
    initial: DrawerSnapPoints.HALF_OPENED,
  },
};

const getDrawerConfiguration = (
  activePanel: MobileDrawerStoreState['activePanel']
): SnapPointsConfig => {
  return (
    PANEL_CONFIGURATIONS[activePanel as keyof typeof PANEL_CONFIGURATIONS] ||
    PANEL_CONFIGURATIONS.default
  );
};

const getDrawerSnapPointsState = (
  isOpen: boolean,
  activePanel: MobileDrawerStoreState['activePanel'],
  prevSnapPoint: DrawerSnapPoints
): Pick<MobileDrawerStoreState, 'snapPoint' | 'availableSnapPoints'> => {
  const config = getDrawerConfiguration(activePanel);

  return {
    snapPoint: isOpen ? config.initial : prevSnapPoint,
    availableSnapPoints: config.available ?? DEFAULT_SNAP_POINTS,
  };
};

export const useMobileDrawerStore = create<MobileDrawerStoreState>((set) => ({
  // State
  isOpen: false,
  snapPoint: DrawerSnapPoints.HALF_OPENED,
  availableSnapPoints: DEFAULT_SNAP_POINTS,
  activePanel: null,

  // Actions
  setState: (newState): void =>
    set((prevState) => ({
      ...prevState,
      ...newState,
      ...getDrawerSnapPointsState(
        newState.isOpen ?? false,
        newState.activePanel ?? prevState.activePanel,
        prevState.snapPoint
      ),
    })),
  setIsOpen: (isOpen): void =>
    set((prevState) => ({
      ...prevState,
      isOpen,
      ...getDrawerSnapPointsState(
        isOpen,
        prevState.activePanel,
        prevState.snapPoint
      ),
    })),
  setSnapPoint: (snapPoint): void =>
    set((prevState) => ({ ...prevState, snapPoint })),
}));
